// Island for /scan: validates the address, loads Turnstile on demand, starts the scan and streams
// progress over SSE, then opens the report.
import { readScanHandle, type ScanHandle, saveScanHandle, watchScan } from './scan-resume.ts';
import { postJson, progressLabel, readBoot, turnstileToken } from './shared.ts';

interface Boot {
  turnstileSiteKey: string | null;
}

const boot = readBoot<Boot>() ?? { turnstileSiteKey: null };
const form = document.getElementById('scan-form') as HTMLFormElement | null;
const input = document.getElementById('scan-url') as HTMLInputElement | null;
const errorEl = document.getElementById('scan-error');
const progress = document.getElementById('scan-progress');
const slot = document.getElementById('turnstile-slot');

function setError(msg: string) {
  if (errorEl) errorEl.textContent = msg;
  input?.setAttribute('aria-invalid', msg ? 'true' : 'false');
}

function addStep(text: string) {
  if (!progress) return;
  const li = document.createElement('li');
  li.textContent = text;
  progress.appendChild(li);
}

function normalise(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (!u.hostname.includes('.')) return null;
    return u.toString();
  } catch {
    return null;
  }
}

const STORAGE_KEY = 'growth_inbound_scan';
const button = form?.querySelector('button[type="submit"]') as HTMLButtonElement | null;
let stopWatching: (() => void) | null = null;
function resume(handle: ScanHandle) {
  stopWatching?.();
  if (button) button.disabled = true;
  saveScanHandle(STORAGE_KEY, handle);
  stopWatching = watchScan(handle, {
    connection: setError,
    event: (event) => {
      const label = progressLabel(event);
      if (label && event.type !== 'error') addStep(label);
      if (event.type === 'done' || event.type === 'error') {
        saveScanHandle(STORAGE_KEY, null);
        if (button) button.disabled = false;
        if (event.type === 'done' && event.report_url) window.location.assign(event.report_url);
        else setError(event.message ?? 'The report is unavailable. You can start a new scan.');
      }
    },
  });
}
interface PendingScan {
  requestKey: string;
  url: string;
}
const PENDING_KEY = 'growth_pending_scan_request';
function pendingScan(): PendingScan | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? 'null');
    return value && /^[0-9a-f-]{36}$/.test(value.requestKey) && typeof value.url === 'string' ? value : null;
  } catch {
    return null;
  }
}
function savePending(value: PendingScan | null) {
  try {
    if (value) sessionStorage.setItem(PENDING_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* Current page can still recover using the in-memory request. */
  }
}
let recovering: ReturnType<typeof setTimeout> | null = null;
function recover(pending: PendingScan) {
  if (recovering) clearTimeout(recovering);
  if (button) button.disabled = true;
  if (input) input.value = pending.url;
  let attempts = 0;
  const lookup = async () => {
    attempts++;
    try {
      const response = await fetch(`/demo/scan/request/${encodeURIComponent(pending.requestKey)}`, {
        credentials: 'same-origin',
      });
      if (response.ok) {
        const found = (await response.json()) as { job_id: string; session_id: string };
        resume({ jobId: found.job_id, sessionKey: found.session_id });
        savePending(null);
        return;
      }
      if ([401, 403, 410].includes(response.status)) {
        savePending(null);
        setError('This scan request is no longer available to this browser. Start a new scan.');
        if (button) button.disabled = false;
        return;
      }
    } catch {
      /* A lost response does not authorize another submission. */
    }
    if (attempts < 30) {
      setError('Recovering your existing scan request…');
      recovering = setTimeout(() => {
        void lookup();
      }, 1_000);
    } else {
      setError('The scan request could not be confirmed. Trying this page again will reuse the same request.');
      if (button) button.disabled = false;
    }
  };
  void lookup();
}
const previous = readScanHandle(STORAGE_KEY);
const pending = pendingScan();
if (previous) resume(previous);
else if (pending) recover(pending);

form?.addEventListener('submit', async (event) => {
  event.preventDefault();
  setError('');
  const url = normalise(input?.value ?? '');
  if (!url) {
    setError('Enter a full page address, for example https://www.example.com/');
    input?.focus();
    return;
  }
  if (button) button.disabled = true;
  progress?.replaceChildren();
  try {
    let token: string | undefined;
    if (boot.turnstileSiteKey && slot) {
      addStep('Quick verification…');
      token = await turnstileToken(slot, boot.turnstileSiteKey);
    }
    const previousRequest = pendingScan();
    const request = previousRequest?.url === url ? previousRequest : { requestKey: crypto.randomUUID(), url };
    savePending(request);
    const res = await postJson<{ job_id?: string; session_id?: string; error?: string }>('/demo/scan', {
      url,
      turnstile_token: token,
      request_key: request.requestKey,
    });
    if (!res.ok || !res.data.job_id || !res.data.session_id) {
      setError(res.data?.error ?? 'The scan could not start. Please try again.');
      if (button) button.disabled = false;
      return;
    }
    resume({ jobId: res.data.job_id, sessionKey: res.data.session_id });
    savePending(null);
  } catch (error) {
    const pending = pendingScan();
    if (pending) recover(pending);
    else {
      setError((error as Error).message);
      if (button) button.disabled = false;
    }
  }
});
